import { PIPELINE_MAP } from 'model/deals.model';
import { useEffect, useMemo, useState } from 'react';
import { DealsService } from 'service/deals.service';
import { normalizeDealType } from '../DealsDetails.utils';

export type OwnerOption = { value: string; label: string };

export const useDealDetailsData = (isOpen: boolean, deal?: any) => {
  const [dealData, setDealData] = useState<any>(deal);
  const [loading, setLoading] = useState(false);
  const [ownerOptions, setOwnerOptions] = useState<OwnerOption[]>([]);
  const [stageOptions, setStageOptions] = useState<string[]>([]);

  const normalizedDealType = useMemo(() => {
    const pipelineType = dealData?.pipelineType || dealData?.dealType || '';
    return normalizeDealType(pipelineType);
  }, [dealData?.pipelineType, dealData?.dealType]);

  useEffect(() => {
    if (!normalizedDealType || !isOpen) {
      setStageOptions([]);
      return;
    }

    const fetchStages = async () => {
      try {
        const dealTypeKey = Object.keys(PIPELINE_MAP).find(
          (key) => PIPELINE_MAP[key] === normalizedDealType,
        );

        if (dealTypeKey) {
          const dropdownsData = await DealsService.getDealDropdowns(dealTypeKey);
          if (dropdownsData.stages && Array.isArray(dropdownsData.stages)) {
            const stages = dropdownsData.stages.map((stage: any) => stage.stageName || stage);
            setStageOptions(stages);
          } else {
            setStageOptions([]);
          }
        } else {
          setStageOptions([]);
        }
      } catch (error) {
        console.error('Failed to fetch stages:', error);
        setStageOptions([]);
      }
    };

    fetchStages();
  }, [normalizedDealType, isOpen]);

  useEffect(() => {
    if (isOpen && deal) {
      const dealId = deal.id || deal.dealId;
      setDealData(deal);
      if (dealId) {
        setLoading(true);
        DealsService.getDealById(dealId)
          .then((response) => {
            const dealData = response?.data || response;
            setDealData({ ...deal, ...dealData });
            setLoading(false);
          })
          .catch((error) => {
            console.error('Error fetching deal:', error);
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    } else if (!isOpen) {
      setDealData(null);
    }
  }, [isOpen, deal]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchOwners = async () => {
      try {
        const data = await DealsService.getMembers();
        const members = data?.members || (Array.isArray(data) ? data : []);
        const formattedOwners = members
          .map((item: any) => ({
            value: `${item.firstName} ${item.lastName}` || '',
            label: `${item.firstName} ${item.lastName}` || '',
          }))
          .filter((item: any) => item.value && item.label);
        setOwnerOptions(formattedOwners);
      } catch (error) {
        console.error('Failed to fetch owners', error);
        setOwnerOptions([]);
      }
    };

    fetchOwners();
  }, [isOpen]);

  return {
    dealData,
    setDealData,
    loading,
    ownerOptions,
    stageOptions,
    normalizedDealType,
  };
};
